import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import {
  combineLatest,
  concat,
  forkJoin,
  Observable,
  of,
  ReplaySubject
} from 'rxjs';
import {
  catchError,
  map,
  mergeMap,
  shareReplay,
  startWith,
  tap
} from 'rxjs/operators';
import {
  InteractionsApi,
  PicturesAPI,
  VendorAPI,
  VendorFormatService,
  IVendorDocumentBase,
  IVendorPicture,
  IVendorPictureBase,
  SizeCode,
  VendorModelResponse,
  VendorProfile,
  VendorState
} from 'sustainment-component';
import { ConnectionStatus } from 'sustainment-component/lib/types/interactions';
import { EMPTY_GUID } from 'src/app/utils/contants';

// App-wide "no image" placeholder, copied into the build from the component
// library's assets (see angular.json). Used when a picture has no real file.
const NO_IMAGE_URL = 'assets/logos/NoLogo.png';

@Injectable({ providedIn: 'root' })
export class VendorService {
  public constructor(
    private _sanitizer: DomSanitizer,
    private _apiVendor: VendorAPI,
    private _interactionsApi: InteractionsApi,
    private _vendorFormatService: VendorFormatService,
    private _pictureAPI: PicturesAPI
  ) {}

  public loadVendor(id: string, loadData = true): Observable<VendorState> {
    const subject = new ReplaySubject<VendorState>(1);

    const vendorData$ = this._apiVendor.getVendorData(id);

    const vendorConnectionStatus$ = vendorData$.pipe(
      mergeMap((vendor) =>
        this._interactionsApi.getConnectionStatus(id).pipe(
          catchError(
            (): Observable<ConnectionStatus> => of({ status: 'None', code: '' })
          ),
          map((is) => ({
            ...vendor,
            connected: is.status === 'Connected',
            inviteRequested: is.status === 'InviteRequested',
            connectionRequested: is.status === 'ConnectionRequested',
            wantsToConnect: is.status === 'WantsToConnect'
          }))
        )
      )
    );

    const initVendorState$ = vendorConnectionStatus$.pipe(
      tap((v) =>
        subject.next({
          vendor: this.calculateVendorState(v),
          pictures: [],
          pdfDocuments: []
        })
      )
    );

    let thumbnails$: Observable<IVendorPicture[]>;
    let pdfData$: Observable<IVendorDocumentBase[]>;
    if (loadData) {
      const loadingPictures$ = this._apiVendor.getVendorPicturesById(id).pipe(
        startWith<IVendorPictureBase[]>([]),
        map((pictures) => {
          return pictures.map((p) => ({
            ...p,
            url: '/assets/icons/loading.gif'
          }));
        }),
        shareReplay(1)
      );

      thumbnails$ = loadingPictures$.pipe(
        mergeMap((pictures) => forkJoin(this.getThumbnails(pictures, id)))
      );
      thumbnails$ = concat(loadingPictures$, thumbnails$);
      pdfData$ = this._apiVendor.getVendorDocumentsById(id).pipe(startWith([]));
    } else {
      thumbnails$ = of([]);
      pdfData$ = of([]);
    }

    const vendorState$ = combineLatest([
      initVendorState$,
      thumbnails$,
      pdfData$
    ]).pipe(
      map(([vendor, thumbnails, pdfData]) => {
        return {
          vendor: this.calculateVendorState(vendor),
          pictures: thumbnails,
          pdfDocuments: pdfData
        };
      }),
      tap((state) => subject.next(state))
    );

    vendorState$.subscribe({ complete: () => subject.complete() });

    return subject.asObservable();
  }

  public getThumbnails(
    pictures: IVendorPictureBase[],
    sustainmentId: string
  ): Observable<IVendorPicture>[] {
    return pictures.map((p: IVendorPictureBase) => {
      // Picture rows without a real file come back with an empty or all-zero
      // externalId; skip the Storage call (it would 404 on /file/{EMPTY_GUID})
      // and render the placeholder directly.
      if (!p.externalId || p.externalId === EMPTY_GUID) {
        return of<IVendorPicture>({
          ...p,
          url: this._sanitizer.bypassSecurityTrustUrl(NO_IMAGE_URL)
        });
      }

      return this._pictureAPI
        .getPicture(p.externalId, sustainmentId, SizeCode.Thumbnail)
        .pipe(
          catchError(() => of(NO_IMAGE_URL)),
          map((b: Blob | string): IVendorPicture => {
            const url = b instanceof Blob ? URL.createObjectURL(b) : b;
            return { ...p, url: this._sanitizer.bypassSecurityTrustUrl(url) };
          })
        );
    });
  }

  //TODO: we need the type, not the same when own profile vs someone else
  private calculateVendorState(vendor: VendorModelResponse): VendorProfile {
    return {
      ...vendor,
      certifications: vendor.certifications || [],
      classifications: vendor.classifications || [],
      logoUrl: this._vendorFormatService.getLogo(
        vendor.sustainmentId!,
        vendor.logo!
      ),
      coverUrl: this._vendorFormatService.getCover(
        vendor.sustainmentId!,
        vendor.cover!
      )
    } as unknown as VendorProfile;
  }
}
